import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'
import { getFirestore, collection, getDocs, addDoc, setDoc, updateDoc, doc, getDoc, deleteDoc, where, query, writeBatch, orderBy, startAfter, limit } from "firebase/firestore"
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions"
import { now } from 'moment'
import config from "../../config.json"
import firebaseConfigs from "./firebaseConfigs.json"
import { EMAIL_SENDINGS } from '../constants/collections'
import {fetchFilteredEmailSendings} from './emailSendingQueries'

const app = initializeApp(firebaseConfigs[config.environment]);

const auth = getAuth(app)
const firestore = getFirestore(app)
const functions = getFunctions(app, "europe-west1")

if(config.useFunctionsEmulator) {
  connectFunctionsEmulator(functions, "localhost", 5001)
}

const createDocument = async (collectionPath, data) => {
  const timestamp = now()
  data.meta = {
    created: timestamp,
    updated: timestamp
  }
  delete data.id
  return await addDoc(collection(firestore, collectionPath), data)
}

const createDocumentWithId = async (collectionPath, documentId, d) => {
  const timestamp = now()
  const data = {
    ...d,
    meta: {
      created: timestamp,
      updated: timestamp
    }
  }
  delete data.id
  await setDoc(doc(firestore, collectionPath, documentId), data)
  return { id: documentId }
}

const updateDocument = async (documentPath, d) => {
  const timestamp = now()
  let data = { ...d }
  if(!data.meta) {
    data.meta = {
      created: timestamp,
      updated: timestamp
    }
  } else {
    data.meta.updated = timestamp
  }
  delete data.id
  let response
  try {
    // response = await setDoc(doc(firestore, documentPath), data, { merge: true })
    response = await updateDoc(doc(firestore, documentPath), data)
  } catch(err) {
    console.log("error updating document", err)
    return err
  }
  return response
}

const updateBatch = async (updatesMap) => {
  const batch = writeBatch(firestore)
  for (const [path, data] of Object.entries(updatesMap)) {
    const ref = doc(firestore, path)
    batch.update(ref, data)
  }
  try {
    await batch.commit()
  } catch(err) {
    console.log("error updating batch", err)
    return err
  }
}

const deleteDocument = async (documentPath) => {
  return await deleteDoc(doc(firestore, documentPath))
}


const fetchCollection = async (collectionPath, collectionQuery) => {
  try {
    if(Array.isArray(collectionQuery) && collectionQuery.length > 0) {
      const constraints = collectionQuery.map((q) => where(q.field, q.op, q.value))
      return await getDocs(query(collection(firestore, collectionPath), ...constraints))
    }
    if(collectionQuery) {
      return await getDocs(query(collection(firestore, collectionPath),  where(collectionQuery.field, collectionQuery.op, collectionQuery.value)))
    }
    return await getDocs(collection(firestore, collectionPath))
  } catch(err) {
    console.error("error fetching collection", collectionPath)
    console.error(err)
    return null
  }
}

const fetchDocument = async (documentPath) => {
  return await getDoc(doc(firestore, documentPath))
}

export const EMAIL_SENDINGS_PAGE_SIZE = 25

const requireEmailTrackingEnvironment = () => {
  if(config.environment !== 'production') {
    throw new Error('Email tracking is available only in production admin.')
  }
}

const fetchEmailTrackingPage = async (path, sortField, cursor = null) => {
  requireEmailTrackingEnvironment()
  const constraints = [orderBy(sortField, 'desc')]
  if(cursor) constraints.push(startAfter(cursor))
  constraints.push(limit(EMAIL_SENDINGS_PAGE_SIZE + 1))
  const snapshot = await getDocs(query(collection(firestore, path), ...constraints))
  const displayed = snapshot.docs.slice(0, EMAIL_SENDINGS_PAGE_SIZE)
  return {
    records: displayed.map(item => ({...item.data(), id: item.id})),
    nextCursor: displayed[displayed.length - 1] || null,
    hasNext: snapshot.docs.length > EMAIL_SENDINGS_PAGE_SIZE,
  }
}

export const fetchEmailSendings = (cursor = null, {status = '', environment = ''} = {}) => {
  requireEmailTrackingEnvironment()
  if(status || environment) {
    return fetchFilteredEmailSendings({
      auth, projectId: app.options.projectId, cursor, status, environment,
      pageSize: EMAIL_SENDINGS_PAGE_SIZE,
    })
  }
  return fetchEmailTrackingPage(EMAIL_SENDINGS, 'meta.updated', cursor)
}

export const fetchEmailSendingEvents = (sendingId, cursor = null) =>
  fetchEmailTrackingPage(`${EMAIL_SENDINGS}/${sendingId}/events`, 'event_at', cursor)

export const fetchEmailSendingDetails = async (sendingId) => {
  requireEmailTrackingEnvironment()
  const sending = await getDoc(doc(firestore, EMAIL_SENDINGS, sendingId))
  if(!sending.exists()) return null
  const recipients = await getDocs(collection(firestore, EMAIL_SENDINGS, sendingId, 'recipients'))
  return {
    sending: {...sending.data(), id: sending.id},
    recipients: recipients.docs.map(item => ({...item.data(), id: item.id}))
      .sort((a, b) => (a.address || '').localeCompare(b.address || '')),
  }
}

const retrieveCustomers = async () => {
  const fetchFunction = httpsCallable(functions, "fetch_customers")
  const res = await fetchFunction()
  return res.data.customers
}

const uploadFile = async (data) => {
  const uploadFunction = httpsCallable(functions, "admin_upload_file")
  const res = await uploadFunction(data)
  return res.data
}

const downloadTemplate = async (templateId) => {
  const downloadFunction = httpsCallable(functions, "admin_download_template")
  const res = await downloadFunction({ templateId })
  return res.data
}

const createCollectionBackup = async (collectionName, environment) => {
  const backupFunction = httpsCallable(functions, "create_collection_backup")
  const res = await backupFunction({ collectionName, environment })
  return res.data
}

const importCollection = async (collectionName, sourceEnvironment, targetEnvironment, strategy) => {
  const importFunction = httpsCallable(functions, "import_collection")
  const res = await importFunction({ collectionName, sourceEnvironment, targetEnvironment, strategy })
  return res.data
}

const fetchStagingArticle = async (path) => {
  const fetchFunction = httpsCallable(functions, "fetch_staging_article")
  const res = await fetchFunction({ path })
  return res.data.article
}

const fixPurchaseDocuments = async (purchaseId, documentType = 'purchase') => {
  const fixFunction = httpsCallable(functions, "fix_purchase_documents")
  const res = await fixFunction({ purchaseId, documentType })
  return res.data
}

const resendConfirmationEmail = async (purchaseId, message = "") => {
  const resendFunction = httpsCallable(functions, "resend_confirmation_email")
  const res = await resendFunction({ purchaseId, customMessage: message })
  return res.data
}

const sendPurchaseOrderReminder = async (orderId) => {
  console.log("sending purchase order reminder", orderId)
  const reminderFunction = httpsCallable(functions, "remind_purchase_order_manual")
  const res = await reminderFunction({ orderId })
  return res.data
}

const rebuildAnalytics = async () => {
  const rebuildFunction = httpsCallable(functions, "rebuild_analytics")
  const res = await rebuildFunction()
  return res.data
}

const retrieveAnalytics = async ({ force = false } = {}) => {
  const retrieveFunction = httpsCallable(functions, "retrieve_analytics", { timeout: 120000 })
  const res = await retrieveFunction({ force })
  return res.data
}

export default app

export {
  auth,
  functions,
  signInWithEmailAndPassword,
  createDocument,
  createDocumentWithId,
  updateDocument,
  updateBatch,
  deleteDocument,
  fetchCollection,
  fetchDocument,
  retrieveCustomers,
  uploadFile,
  downloadTemplate,
  createCollectionBackup,
  importCollection,
  fetchStagingArticle,
  fixPurchaseDocuments,
  resendConfirmationEmail,
  sendPurchaseOrderReminder,
  rebuildAnalytics,
  retrieveAnalytics
}
